import React, { useState, useEffect, useRef, useCallback } from 'react';
import { AlertCircle, CheckCircle, Calculator, Hash, X } from 'lucide-react';
import axios from '../lib/axios';

/**
 * FormulaEditor - Editor de fórmulas con autocompletado
 * 
 * Características:
 * - Autocomplete de columnas al escribir #
 * - Autocomplete de operadores al escribir espacio
 * - Validación en tiempo real
 * - Preview de columnas utilizadas
 * - Sintaxis highlighting básica
 */
const FormulaEditor = ({ 
  value = '', 
  onChange, 
  onValidation,
  placeholder = 'Escribe # para autocompletar columnas, espacio para operadores...',
  disabled = false 
}) => {
  const [formula, setFormula] = useState(value);
  const [suggestions, setSuggestions] = useState([]);
  const [suggestType, setSuggestType] = useState(null); // 'columnas' | 'operadores'
  const [cursorPosition, setCursorPosition] = useState(0);
  const [selectedSuggestion, setSelectedSuggestion] = useState(0);
  const [validationStatus, setValidationStatus] = useState({ valid: null, message: '', columnas: [] });
  const [showSuggestions, setShowSuggestions] = useState(false);
  
  const inputRef = useRef(null);
  const suggestionsRef = useRef(null);
  const debounceTimeout = useRef(null);

  // Catálogo de columnas disponibles (se puede cargar desde API)
  const [columnasDisponibles, setColumnasDisponibles] = useState([]);

  // Operadores disponibles
  const operadores = [
    { value: '+', label: '+ (Suma)', group: 'Aritméticos' },
    { value: '-', label: '- (Resta)', group: 'Aritméticos' },
    { value: '*', label: '* (Multiplicación)', group: 'Aritméticos' },
    { value: '/', label: '/ (División)', group: 'Aritméticos' },
    { value: '(', label: '( (Paréntesis)', group: 'Aritméticos' },
    { value: ')', label: ') (Paréntesis)', group: 'Aritméticos' },
    { value: '==', label: '== (Igual)', group: 'Comparación' },
    { value: '!=', label: '!= (Distinto)', group: 'Comparación' },
    { value: '>', label: '> (Mayor que)', group: 'Comparación' },
    { value: '<', label: '< (Menor que)', group: 'Comparación' },
    { value: '>=', label: '>= (Mayor o igual)', group: 'Comparación' },
    { value: '<=', label: '<= (Menor o igual)', group: 'Comparación' },
    { value: 'AND', label: 'AND (Y lógico)', group: 'Lógicos' },
    { value: 'OR', label: 'OR (O lógico)', group: 'Lógicos' },
    { value: 'NOT', label: 'NOT (Negación)', group: 'Lógicos' },
    { value: 'IF(', label: 'IF(cond, true, false)', group: 'Funciones' },
    { value: 'MAX(', label: 'MAX(a, b, ...)', group: 'Funciones' },
    { value: 'MIN(', label: 'MIN(a, b, ...)', group: 'Funciones' },
    { value: 'ABS(', label: 'ABS(valor)', group: 'Funciones' },
    { value: 'ROUND(', label: 'ROUND(valor, decimales)', group: 'Funciones' },
  ];

  // Cargar columnas disponibles al montar
  useEffect(() => {
    const fetchColumnas = async () => {
      try {
        const response = await axios.get('/api/contratos/columnas-filtrables');
        const data = response.data;
        
        if (data.data) {
          // Convertir a array de objetos {name, type}
          const cols = Object.entries(data.data).map(([name, type]) => ({
            name,
            type,
            label: `#${name} (${type})`
          }));
          setColumnasDisponibles(cols);
        }
      } catch (error) {
        console.error('Error cargando columnas:', error);
      }
    };

    fetchColumnas();
  }, []);

  // Sincronizar con prop externa
  useEffect(() => {
    if (value !== formula) {
      setFormula(value);
    }
  }, [value]);

  // Validar fórmula con debounce
  useEffect(() => {
    if (!formula.trim()) {
      setValidationStatus({ valid: null, message: '', columnas: [] });
      if (onValidation) onValidation(null, '', []);
      return;
    }

    // Clear previous timeout
    if (debounceTimeout.current) {
      clearTimeout(debounceTimeout.current);
    }

    // Set new timeout
    debounceTimeout.current = setTimeout(async () => {
      try {
        const response = await axios.post('/api/contratos/validar-formula', {
          formula_calculo: formula
        });

        const data = response.data;

        setValidationStatus({ 
          valid: true, 
          message: data.message || 'Fórmula válida', 
          columnas: data.columnas || [] 
        });
        if (onValidation) onValidation(true, data.message, data.columnas);
      } catch (error) {
        console.error('Error validando fórmula:', error);
        const errorMsg = error.response?.data?.error || 'Error validando fórmula';
        setValidationStatus({ 
          valid: false, 
          message: errorMsg, 
          columnas: [] 
        });
        if (onValidation) onValidation(false, errorMsg, []);
      }
    }, 500); // Debounce de 500ms

    return () => {
      if (debounceTimeout.current) {
        clearTimeout(debounceTimeout.current);
      }
    };
  }, [formula, onValidation]);

  // Detectar # o espacio para mostrar sugerencias
  const handleInputChange = (e) => {
    const newValue = e.target.value;
    const newCursorPos = e.target.selectionStart;

    setFormula(newValue);
    setCursorPosition(newCursorPos);
    if (onChange) onChange(newValue);

    // Detectar # para columnas
    const textBeforeCursor = newValue.substring(0, newCursorPos);
    const lastHashIndex = textBeforeCursor.lastIndexOf('#');
    
    if (lastHashIndex !== -1) {
      const searchTerm = textBeforeCursor.substring(lastHashIndex + 1);
      
      // Solo mostrar si no hay espacio después del #
      if (!searchTerm.includes(' ')) {
        const filtered = columnasDisponibles.filter(col =>
          col.name.toLowerCase().includes(searchTerm.toLowerCase())
        );
        
        setSuggestions(filtered);
        setSuggestType('columnas');
        setShowSuggestions(filtered.length > 0);
        setSelectedSuggestion(0);
        return;
      }
    }

    // Detectar espacio para operadores (solo si no está dentro de un string)
    if (newValue[newCursorPos - 1] === ' ') {
      // Verificar que no estamos dentro de comillas
      const beforeSpace = newValue.substring(0, newCursorPos - 1);
      const singleQuotes = (beforeSpace.match(/'/g) || []).length;
      const doubleQuotes = (beforeSpace.match(/"/g) || []).length;
      
      // Si hay número impar de comillas, estamos dentro de un string
      if (singleQuotes % 2 === 0 && doubleQuotes % 2 === 0) {
        setSuggestions(operadores);
        setSuggestType('operadores');
        setShowSuggestions(true);
        setSelectedSuggestion(0);
        return;
      }
    }

    setShowSuggestions(false);
  };

  // Manejar selección de sugerencia
  const handleSelectSuggestion = useCallback((suggestion) => {
    const input = inputRef.current;
    if (!input) return;

    const textBeforeCursor = formula.substring(0, cursorPosition);
    const textAfterCursor = formula.substring(cursorPosition);

    let newFormula;
    let newCursorPos;

    if (suggestType === 'columnas') {
      // Reemplazar desde el último # hasta el cursor
      const lastHashIndex = textBeforeCursor.lastIndexOf('#');
      newFormula = 
        formula.substring(0, lastHashIndex) + 
        '#' + suggestion.name + 
        textAfterCursor;
      newCursorPos = lastHashIndex + suggestion.name.length + 1;
    } else {
      // Operadores: insertar después del espacio
      newFormula = 
        textBeforeCursor + 
        suggestion.value + ' ' +
        textAfterCursor;
      newCursorPos = cursorPosition + suggestion.value.length + 1;
    }

    setFormula(newFormula);
    if (onChange) onChange(newFormula);
    setShowSuggestions(false);

    // Restaurar foco y posición del cursor
    setTimeout(() => {
      input.focus();
      input.setSelectionRange(newCursorPos, newCursorPos);
    }, 0);
  }, [formula, cursorPosition, suggestType, onChange]);

  // Navegación por teclado en sugerencias
  const handleKeyDown = (e) => {
    if (!showSuggestions) return;

    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        setSelectedSuggestion(prev => 
          prev < suggestions.length - 1 ? prev + 1 : prev
        );
        break;
      case 'ArrowUp':
        e.preventDefault();
        setSelectedSuggestion(prev => prev > 0 ? prev - 1 : 0);
        break;
      case 'Enter':
      case 'Tab':
        e.preventDefault();
        if (suggestions[selectedSuggestion]) {
          handleSelectSuggestion(suggestions[selectedSuggestion]);
        }
        break;
      case 'Escape':
        setShowSuggestions(false);
        break;
      default:
        break;
    }
  };

  // Cerrar sugerencias al hacer click fuera
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (suggestionsRef.current && !suggestionsRef.current.contains(e.target) &&
          inputRef.current && !inputRef.current.contains(e.target)) {
        setShowSuggestions(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Agrupar operadores por categoría
  const operadoresAgrupados = operadores.reduce((acc, op) => {
    if (!acc[op.group]) acc[op.group] = [];
    acc[op.group].push(op);
    return acc;
  }, {});

  return (
    <div className="formula-editor relative">
      {/* Input principal */}
      <div className="relative">
        <textarea
          ref={inputRef}
          value={formula}
          onChange={handleInputChange}
          onKeyDown={handleKeyDown}
          placeholder={placeholder}
          disabled={disabled}
          className={`
            w-full px-4 py-3 pr-10
            bg-slate-800 border rounded-lg
            text-slate-100 text-sm font-mono
            placeholder-slate-500
            focus:outline-none focus:ring-2 focus:ring-primary/50
            resize-y min-h-[80px]
            ${validationStatus.valid === true ? 'border-emerald-500/50' : ''}
            ${validationStatus.valid === false ? 'border-red-500/50' : 'border-slate-700'}
            ${disabled ? 'opacity-50 cursor-not-allowed' : ''}
          `}
        />
        
        {/* Icono de calculadora */}
        <Calculator className="absolute top-3 right-3 text-slate-500" size={18} />
      </div>

      {/* Sugerencias */}
      {showSuggestions && suggestions.length > 0 && (
        <div
          ref={suggestionsRef}
          className="absolute z-50 w-full mt-1 bg-slate-800 border border-slate-700 rounded-lg shadow-xl max-h-64 overflow-y-auto"
        >
          {suggestType === 'columnas' ? (
            // Sugerencias de columnas
            suggestions.map((col, idx) => (
              <button
                key={col.name}
                onClick={() => handleSelectSuggestion(col)}
                className={`
                  w-full text-left px-4 py-2 text-sm
                  flex items-center gap-2
                  hover:bg-slate-700 transition-colors
                  ${idx === selectedSuggestion ? 'bg-slate-700' : ''}
                `}
              >
                <Hash size={14} className="text-primary flex-shrink-0" />
                <span className="font-mono text-slate-100">{col.name}</span>
                <span className="text-xs text-slate-500 ml-auto">{col.type}</span>
              </button>
            ))
          ) : (
            // Sugerencias de operadores (agrupadas)
            Object.entries(operadoresAgrupados).map(([group, ops]) => (
              <div key={group}>
                <div className="px-4 py-1 text-xs font-semibold text-slate-500 bg-slate-900/50">
                  {group}
                </div>
                {ops.map((op, idx) => {
                  const globalIdx = operadores.findIndex(o => o.value === op.value);
                  return (
                    <button
                      key={op.value}
                      onClick={() => handleSelectSuggestion(op)}
                      className={`
                        w-full text-left px-4 py-2 text-sm
                        hover:bg-slate-700 transition-colors
                        ${globalIdx === selectedSuggestion ? 'bg-slate-700' : ''}
                      `}
                    >
                      <span className="font-mono text-primary">{op.value}</span>
                      <span className="text-xs text-slate-400 ml-2">{op.label}</span>
                    </button>
                  );
                })}
              </div>
            ))
          )}
        </div>
      )}

      {/* Estado de validación */}
      {validationStatus.valid !== null && (
        <div className={`
          mt-2 px-3 py-2 rounded-lg text-sm flex items-start gap-2
          ${validationStatus.valid 
            ? 'bg-emerald-500/10 border border-emerald-500/30 text-emerald-400' 
            : 'bg-red-500/10 border border-red-500/30 text-red-400'}
        `}>
          {validationStatus.valid ? (
            <CheckCircle size={16} className="mt-0.5 flex-shrink-0" />
          ) : (
            <AlertCircle size={16} className="mt-0.5 flex-shrink-0" />
          )}
          <span className="flex-1">{validationStatus.message}</span>
        </div>
      )}

      {/* Preview de columnas utilizadas */}
      {validationStatus.valid && validationStatus.columnas.length > 0 && (
        <div className="mt-3 p-3 bg-slate-900/50 border border-slate-700 rounded-lg">
          <div className="text-xs font-semibold text-slate-400 mb-2">
            Columnas utilizadas ({validationStatus.columnas.length}):
          </div>
          <div className="flex flex-wrap gap-2">
            {validationStatus.columnas.map(col => (
              <span
                key={col}
                className="px-2 py-1 bg-primary/10 border border-primary/30 rounded text-xs font-mono text-primary"
              >
                #{col}
              </span>
            ))}
          </div>
        </div>
      )}

      {/* Ayuda rápida */}
      <div className="mt-2 text-xs text-slate-500">
        <strong>Tip:</strong> Escribe <code className="px-1 py-0.5 bg-slate-800 rounded">#</code> para autocompletar columnas, 
        <code className="px-1 py-0.5 bg-slate-800 rounded mx-1">espacio</code> para operadores.
        Usa ↑↓ para navegar y Enter/Tab para seleccionar.
      </div>
    </div>
  );
};

export default FormulaEditor;
